import { Image, Video, Music, MessageSquare, Link, MousePointerClick } from 'lucide-react';
import type { ContentBlock } from '@/types/flow';

interface BlocksPreviewProps {
    blocks: ContentBlock[];
}

const truncate = (text: string, max: number = 60) =>
    text.length <= max ? text : text.substring(0, max) + '...';

export default function BlocksPreview({ blocks }: BlocksPreviewProps) {
    if (!blocks || blocks.length === 0) return null;

    return (
        <div className="border-t border-dashed border-gray-200 mx-4 pt-2 pb-1 space-y-2">
            {blocks.map((block, i) => (
                <div key={i}>
                    {block.type === 'text' && block.message && (
                        <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-3 py-2 border border-gray-200">
                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                {truncate(block.message, 80)}
                            </p>
                        </div>
                    )}

                    {block.type === 'image' && (
                        <div className="rounded-lg overflow-hidden border border-gray-200">
                            {block.media_url ? (
                                <img
                                    src={block.media_url}
                                    alt={block.media_filename || 'Imagem'}
                                    className="w-full h-20 object-cover"
                                />
                            ) : (
                                <div className="h-16 bg-gray-100 flex items-center justify-center gap-1.5">
                                    <Image className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs text-gray-400">Foto</span>
                                </div>
                            )}
                        </div>
                    )}

                    {block.type === 'video' && (
                        <div className="rounded-lg border border-gray-200 bg-gray-100 h-16 flex items-center justify-center gap-1.5">
                            <Video className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500 truncate max-w-[180px]">
                                {block.media_filename || 'Vídeo'}
                            </span>
                        </div>
                    )}

                    {block.type === 'audio' && (
                        <div className="rounded-lg border border-gray-200 bg-gray-100 h-12 flex items-center justify-center gap-1.5">
                            <Music className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500 truncate max-w-[180px]">
                                {block.media_filename || 'Áudio'}
                            </span>
                        </div>
                    )}

                    {block.type === 'text' && !block.message && (
                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 h-10 flex items-center justify-center gap-1.5">
                            <MessageSquare className="w-3.5 h-3.5 text-gray-300" />
                            <span className="text-xs text-gray-400">Texto vazio</span>
                        </div>
                    )}

                    {/* Block buttons preview */}
                    {block.buttons && block.buttons.length > 0 && (
                        <div className="mt-1 space-y-1">
                            {block.buttons.map((btn, btnIdx) => (
                                <div
                                    key={btnIdx}
                                    className="flex items-center justify-center gap-1 text-xs bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5 text-blue-800 font-medium"
                                >
                                    {btn.type === 'url' ? (
                                        <Link className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                    ) : (
                                        <MousePointerClick className="w-3 h-3 text-green-500 flex-shrink-0" />
                                    )}
                                    <span className="truncate">{btn.text || 'Sem texto'}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
